/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useEffect, FC, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput, { PhoneInputProps } from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useFormikContext } from "formik";
import { FormControl, FormHelperText } from "@mui/material";
import { useMaterialUIController } from "context/ThemeContext";

interface CustomPhoneInputProps extends PhoneInputProps {
  required: boolean;
  name?: string;
  error?: boolean;
}

const FormPhoneInput: FC<CustomPhoneInputProps> = (props) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  const formikContext = useFormikContext();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(props.value || "");
  const [showError, setShowError] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const validatePhone = (value: string): boolean => {
    const numericValue = value ? value.replace(/\D/g, "") : "";
    return (
      !value || numericValue.length === 0 || (numericValue.length >= 7 && numericValue.length <= 15)
    );
  };

  const isRequired = props.required !== false;

  useEffect(() => {
    setLocalValue(props.value || "");
  }, [props.value]);

  useEffect(() => {
    if (formikContext) {
      formikContext.setFieldValue(props.name, localValue, false);

      if (formikContext.submitCount > 0) {
        if (isRequired && (!localValue || !validatePhone(localValue))) {
          setShowError(true);
          setIsTouched(true);
        } else {
          setShowError(false);
        }
      }
    }
  }, [localValue, formikContext?.submitCount, isRequired, props.name]);

  const handleOnChange = (
    value: string,
    country: string | number,
    e: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ): void => {
    setIsEditing(true);
    setLocalValue(value);
    setShowError(false);
    setIsTouched(true);

    if (props.onChange) {
      props.onChange(value, country, e, formattedValue);
    }

    if (formikContext) {
      formikContext.setFieldValue(props.name, value, false);
    }
  };

  const handleOnBlur = () => {
    setIsEditing(false);
    setIsTouched(true);

    if (!isRequired && localValue && !validatePhone(localValue)) {
      setLocalValue("");

      if (formikContext) {
        formikContext.setFieldValue(props.name, "", false);
      }

      if (props.onChange) {
        props.onChange("", null, null, "");
      }
    }
  };

  return (
    <div className={darkMode ? "formPhoneInput-dark-mode" : "formPhoneInput-light-mode"}>
      <FormControl fullWidth error={showError}>
        <PhoneInput
          {...props}
          inputClass="formPhoneInput-inputStyle"
          value={localValue}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          country={"us"}
          isValid={() => {
            if (!isTouched) return true;
            if (isRequired && showError) return false;
            if (!isRequired && !isEditing) return true;
            return validatePhone(localValue) && !props.error;
          }}
        />
        {showError && <FormHelperText>{t("formPhoneInput.wrongPhone")}</FormHelperText>}
      </FormControl>
    </div>
  );
};

export default FormPhoneInput;
